/* hr-copy-guard.js — copy-attribution / scraper-deterrent for body text
 * (owner content-harvest response, 2026-07-24: a visitor spent 1h30m on three
 * brand-story articles and copied 8 chunks of 459-823 chars with zero
 * product-page views -- classic content harvesting, not a customer).
 *
 * HONEST SCOPE: this is a deterrent, not a lock. It never blocks selection,
 * never blocks the native copy shortcut, and never touches short copies (a
 * price, a nutrient figure, a sentence to a friend go through untouched).
 * For copies of 200+ characters it rewrites the clipboard payload to carry a
 * source credit and a backlink, so anything pasted into a document, email,
 * or competitor site still points back to humanrenaissance.ca. Repeat bulk
 * copying in the same session gets a shorter payload -- an excerpt plus an
 * invitation to read the rest on-site -- never a block, never a warning.
 *
 * Sibling to hr-asset-guard.js/.css (image protection). This file is
 * additive and separate -- hr-asset-guard is untouched.
 *
 * PIXEL COORDINATION: hr-conversion-observer (hr-pixel.js) already listens
 * for `copy` on document in the CAPTURE phase and logs content_copy (section
 * + selection length) from the ORIGINAL selection, before this file ever
 * runs -- capture fires top-down before this file's bubble-phase listener,
 * so the pixel's own signal is unaffected by anything below. This file adds
 * a second, richer signal on top of that, through the pixel's own documented
 * public hook -- window.hrTrack(eventName, props), the same function
 * hr-pixel.js exposes for any custom event (see its `window.hrTrack =`
 * assignment). No new endpoint, no direct write to the analytics backend:
 * if window.hrTrack is not present (pixel blocked, consent declined, script
 * failed to load) this file simply skips that call and still does the
 * clipboard rewrite -- see reportToPixel() below.
 *
 * Like hr-asset-guard.js, this listener is bubble phase, never calls
 * stopPropagation()/stopImmediatePropagation(), so nothing about this file
 * can suppress the pixel's own capture-phase listener either.
 *
 * KILL SWITCH: flip HR_COPY_GUARD_ENABLED to false to disable instantly,
 * site-wide, no other change required (mirrors hr-asset-guard.js).
 */
(function () {
  'use strict';

  var HR_COPY_GUARD_ENABLED = true;
  if (!HR_COPY_GUARD_ENABLED) return;

  var QUALIFY_LENGTH = 200; // below this: untouched, no exceptions
  var ESCALATE_AFTER = 3; // 1st-3rd qualifying copy = full rewrite; 4th+ = excerpt
  var EXCERPT_MAX_CHARS = 120;
  var SESSION_KEY = 'hr_copy_guard_count';
  var SKIP_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

  // --------------------------------------------------------------------------
  // Blog articles: hard copy block (owner request 2026-08-05 -- "nothing is
  // copy-pasteable" on the blogs). Everywhere else keeps the existing
  // attribution behaviour: short copies untouched, 200+ char copies credited.
  //
  // Scope is deliberately narrow: only selections whose anchor sits inside an
  // article body, and only on /blogs/ URLs. Form fields and contenteditable
  // are exempt via the same SKIP_SELECTOR the rest of this file uses, so a
  // newsletter box or a search field on an article page still copies normally.
  //
  // Accessibility: this blocks the clipboard event only. The DOM text is
  // untouched, nothing is aria-hidden, no text is rendered as an image, and
  // keyboard navigation and screen-reader virtual cursors are unaffected.
  // Crawlers never fire copy events, so SEO is unaffected.
  // --------------------------------------------------------------------------
  var ARTICLE_SELECTOR = '.hr-article, .hr-article__content, article, .article, .article-template, main .blog-article';

  function isBlogArticleSelection(selection) {
    try {
      if (String(window.location.pathname || '').indexOf('/blogs/') === -1) return false;
      if (!selection || !selection.anchorNode) return false;
      var node = selection.anchorNode;
      if (node.nodeType === 3) node = node.parentElement;
      if (!node || !node.closest) return false;
      if (node.closest(SKIP_SELECTOR)) return false;
      return Boolean(node.closest(ARTICLE_SELECTOR));
    } catch (_error) {
      return false;
    }
  }

  function getCanonicalUrl() {
    try {
      var link = document.querySelector('link[rel="canonical"]');
      if (link && link.href) return link.href;
    } catch (_error) {}
    return window.location.href;
  }

  function getPageTitle() {
    // Prefer the article/page's own heading over the raw <title>, which on
    // this theme often carries a " - Human Renaissance" suffix already; fall
    // back to document.title, then a neutral default. Never invented text.
    try {
      var heading = document.querySelector('article h1, .article h1, main h1, h1');
      var text = heading ? String(heading.textContent || '').replace(/\s+/g, ' ').trim() : '';
      if (text) return text.slice(0, 140);
    } catch (_error) {}
    var docTitle = String(document.title || '').replace(/\s+/g, ' ').trim();
    return docTitle ? docTitle.slice(0, 140) : 'this page';
  }

  function isFormField(target) {
    if (!target || !target.closest) return false;
    try {
      return Boolean(target.closest(SKIP_SELECTOR));
    } catch (_error) {
      return false;
    }
  }

  function sessionCount() {
    try {
      return parseInt(window.sessionStorage.getItem(SESSION_KEY) || '0', 10) || 0;
    } catch (_error) {
      return 0;
    }
  }

  function bumpSessionCount() {
    var next = sessionCount() + 1;
    try {
      window.sessionStorage.setItem(SESSION_KEY, String(next));
    } catch (_error) {}
    return next;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Converts plain text with newlines into safe <br>-joined HTML.
  function textToHtmlLines(value) {
    return escapeHtml(value).replace(/\r\n|\r|\n/g, '<br>');
  }

  // First sentence (up to and including its terminal punctuation) or the
  // first EXCERPT_MAX_CHARS characters, whichever is shorter. Always plain
  // text in, plain text out -- no HTML here.
  function firstSentenceOrExcerpt(text) {
    var sentenceMatch = text.match(/^[\s\S]*?[.!?](?=\s|$)/);
    var sentence = sentenceMatch ? sentenceMatch[0].trim() : '';
    if (sentence && sentence.length <= EXCERPT_MAX_CHARS) return sentence;
    return text.slice(0, EXCERPT_MAX_CHARS).trim();
  }

  function buildPayload(selectionText, escalate, title, url) {
    var bodyText = escalate ? firstSentenceOrExcerpt(selectionText) + '...' : selectionText;
    var sourceLine = 'Source: ' + title + ' - Human Renaissance';

    var textParts = [bodyText, '', sourceLine, url];
    if (escalate) textParts.push('Full article free to read at ' + url);
    var text = textParts.join('\n');

    var htmlParts = [
      textToHtmlLines(bodyText),
      '<br><br>',
      'Source: <a href="' + escapeHtml(url) + '">' + escapeHtml(title) + ' - Human Renaissance</a>',
      '<br>',
      '<a href="' + escapeHtml(url) + '">' + escapeHtml(url) + '</a>',
    ];
    if (escalate) {
      htmlParts.push('<br>Full article free to read at <a href="' + escapeHtml(url) + '">' + escapeHtml(url) + '</a>');
    }
    var html = '<div>' + htmlParts.join('') + '</div>';

    return { text: text, html: html };
  }

  function reportToPixel(charCount, runningCount, escalate) {
    try {
      if (typeof window.hrTrack !== 'function') return; // pixel not present/blocked -- no invented endpoint
      window.hrTrack('copy_guard_rewrite', {
        char_count: Math.min(charCount, 100000),
        session_copy_count: runningCount,
        escalated: Boolean(escalate),
        page_path: window.location.pathname,
        page_title: getPageTitle(),
      });
    } catch (_error) {}
  }

  // --- Selection / right-click / cut deterrent (owner request 2026-08-05) --
  // Pairs with hr-copy-guard.css (user-select:none on body copy). These
  // handlers are the event-level backstop for browsers/paths where CSS alone
  // doesn't stop a "Copy"/"Cut" context-menu action or a programmatic
  // selection. Every one of them bails out immediately on a form field, the
  // cart, or anything inside a <form> -- see isFormField() above -- so
  // discount-code entry, search, address forms, and the cart drawer are
  // completely unaffected. None of this touches Shopify's native checkout
  // (separate origin/template, this script never runs there).
  var CART_OR_FORM_SELECTOR = 'form, cart-drawer, .cart, .cart-drawer, .cart__contents, code, pre, .hr-allow-select';

  function isExemptTarget(target) {
    if (isFormField(target)) return true;
    if (!target || !target.closest) return false;
    try {
      return Boolean(target.closest(CART_OR_FORM_SELECTOR));
    } catch (_error) {
      return false;
    }
  }

  document.addEventListener('selectstart', function (event) {
    try {
      if (isExemptTarget(event.target)) return;
      // Only suppress the drag-to-highlight gesture; never touches focus or
      // keyboard nav, and a screen reader's virtual cursor is unaffected --
      // selectstart only fires on user mouse/touch selection gestures.
      event.preventDefault();
    } catch (_error) {}
  });

  document.addEventListener('contextmenu', function (event) {
    try {
      if (isExemptTarget(event.target)) return;
      event.preventDefault();
    } catch (_error) {}
  });

  document.addEventListener('cut', function (event) {
    try {
      if (isExemptTarget(event.target) || isExemptTarget(document.activeElement)) return;
      event.preventDefault();
    } catch (_error) {}
  });

  document.addEventListener('copy', function (event) {
    try {
      if (isFormField(event.target) || isFormField(document.activeElement)) return;

      var selection = window.getSelection ? window.getSelection() : null;
      var selectionText = selection ? String(selection.toString()) : '';

      // Blog article body: block outright, at any length. See note above.
      if (isBlogArticleSelection(selection)) {
        if (event.clipboardData && typeof event.clipboardData.setData === 'function') {
          event.clipboardData.setData('text/plain', '');
          event.clipboardData.setData('text/html', '');
        }
        event.preventDefault();
        try { selection.removeAllRanges(); } catch (_clearError) {}
        return;
      }

      if (!selectionText || selectionText.length < QUALIFY_LENGTH) return; // real people copying a line: untouched

      if (!event.clipboardData || typeof event.clipboardData.setData !== 'function') return; // fail open, no clipboardData support

      var runningCount = bumpSessionCount();
      var escalate = runningCount > ESCALATE_AFTER;
      var title = getPageTitle();
      var url = getCanonicalUrl();
      var payload = buildPayload(selectionText, escalate, title, url);

      event.clipboardData.setData('text/plain', payload.text);
      event.clipboardData.setData('text/html', payload.html);
      event.preventDefault(); // never stopPropagation() -- the pixel's own capture-phase listener already ran

      reportToPixel(selectionText.length, runningCount, escalate);
    } catch (_error) {
      // Fail open: never swallow a copy the user is entitled to make.
    }
  }); // bubble phase, matches hr-asset-guard.js's convention
})();
